import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = {
  key: 0,
  class: "bottom-fix"
};
import { ref, onMounted, watch, nextTick, computed, onActivated } from 'vue';
import store from '@/store';
import constants from '@/constants';
import cartUtils from './cartUtils';
import { useRoute } from 'vue-router';
import { Bridge } from '@/utils/bridge';
import { useLess } from '@/models/hooks';
import CartTabs from './components/CartTabs.vue';
import CartTopNav from './components/CartTopNav.vue';
import { loadingEvents } from '@/configs/awsRumConfig';
import DiscountDetails from './components/DiscountDetails.vue';
import CheckoutNavigation from './components/CheckoutNavigation.vue';
import FreeShippingTips from './components/FreeShippingTips.vue';
import RecommendedAsins from '@/components/recommendedAsins/RecommendedAsins.vue';
import { setCartDataChangeStatus, getAndResetCartDataChangeStatus } from '@/utils/common';
import { useRecordLoadingEvent } from '@/models/hooks';
import { defineComponent } from 'vue';
const __default__ = defineComponent({
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.cartAreaRef.scrollTop = vm.startTopDistance;
      vm.returnToCartPageHandler();
    });
  }
});
export default /*#__PURE__*/Object.assign(__default__, {
  __name: 'Cart',
  setup(__props, {
    expose: __expose
  }) {
    const {
      FREE_SHIPPING
    } = constants.Common;
    useLess('views/cart/Cart.less');
    useRecordLoadingEvent(duration => ({
      ...loadingEvents.cartPage.cartPage,
      duration
    }));
    const route = useRoute();
    const startTopDistance = ref(0);
    const cartAreaRef = ref(null);
    const totalPricesRef = ref(null);
    const freeShippingTipsRef = ref(null);
    const recommendedAsinsRef = ref(null);
    const isLoadingSelectAddressPopup = ref(true);
    const secondaryCartPageSafetyZoneHeight = ref({});

    // Shopping cart data processing function
    const {
      isLoading,
      freeShipping,
      isManagement,
      isRefreshing,
      allProductData,
      totalPricesStyle,
      isHiddenBottomArea
    } = cartUtils.cartState;
    const {
      getAndUpdateData,
      getAddressListData
    } = cartUtils.handleCartState;

    // phone bottom security height
    const phoneBottomSecurityHeight = computed(() => {
      var _store$state$navigato;
      return (_store$state$navigato = store.state.navigator.phoneBottomSecurityHeight) !== null && _store$state$navigato !== void 0 ? _store$state$navigato : 0;
    });
    const cptPopupHeight = computed(() => {
      var _route$query;
      if ((_route$query = route.query) !== null && _route$query !== void 0 && _route$query.rootPage) {
        return store.state.common.tabbarHeight - phoneBottomSecurityHeight.value;
      }
      return 0;
    });

    // Calculate tabbar height
    const cptTabbarHeight = computed(() => {
      var _route$query2;
      let height = 0;
      if ((_route$query2 = route.query) !== null && _route$query2 !== void 0 && _route$query2.rootPage) {
        height += store.state.common.tabbarHeight;
      }
      if (allProductData.value.length) {
        var _totalPricesRef$value, _totalPricesRef$value2;
        height += (_totalPricesRef$value = totalPricesRef.value) === null || _totalPricesRef$value === void 0 ? void 0 : (_totalPricesRef$value2 = _totalPricesRef$value.element) === null || _totalPricesRef$value2 === void 0 ? void 0 : _totalPricesRef$value2.offsetHeight;
      }
      if (Number(freeShipping.amountFromFSThreshold) >= FREE_SHIPPING.SHOW_FREE_SHIPPING_PROMPT_THRESHOLD && !isManagement.value) {
        var _freeShippingTipsRef$, _freeShippingTipsRef$2;
        height += (_freeShippingTipsRef$ = freeShippingTipsRef.value) === null || _freeShippingTipsRef$ === void 0 ? void 0 : (_freeShippingTipsRef$2 = _freeShippingTipsRef$.element) === null || _freeShippingTipsRef$2 === void 0 ? void 0 : _freeShippingTipsRef$2.offsetHeight;
      }
      return height;
    });
    const onPullRefresh = () => {
      isRefreshing.value = true;
      recommendedAsinsRef.value.getSearch(true);
      onReloadData();
    };
    const onReloadData = async () => {
      isLoading.value = !isRefreshing.value;
      getAndUpdateData(() => {
        isRefreshing.value = false;
      });
    };
    const returnToCartPageHandler = async () => {
      var _route$query3;
      isHiddenBottomArea.value = false;
      isLoadingSelectAddressPopup.value = false;
      nextTick(() => {
        isLoadingSelectAddressPopup.value = true;
      });
      await getAddressListData();
      // Every time you enter the second level shopping cart page, you don't need to judge and directly request data again
      if (!((_route$query3 = route.query) !== null && _route$query3 !== void 0 && _route$query3.rootPage)) {
        secondaryCartPageSafetyZoneHeight.value = phoneBottomSecurityHeight.value;
        totalPricesStyle.value = {
          paddingBottom: `${phoneBottomSecurityHeight.value}px`
        };
        getAndUpdateData();
        return;
      }
      secondaryCartPageSafetyZoneHeight.value = cptPopupHeight.value + phoneBottomSecurityHeight.value;
      totalPricesStyle.value = {
        marginBottom: `${cptPopupHeight.value + phoneBottomSecurityHeight.value}px`
      };
      // Has the shopping cart changed its status
      getAndResetCartDataChangeStatus(async isChanged => {
        if (isChanged) {
          getAndUpdateData(() => {
            setCartDataChangeStatus(false);
          });
        }
      });
    };

    // Listen to the shopping cart list on the current page, refresh, and retrieve data again
    watch(() => store.state.cart.isCurrentCartRefresh, status => {
      if (status) {
        getAndUpdateData();
        store.commit('cart/setCurrentCartRefreshStatus', false);
      }
    });

    // If scrollToTop changes in the listening routing parameters, set the scrollTop on the shopping cart page to 0
    watch(() => route.query, query => {
      if (query.scrollToTop) {
        cartAreaRef.value.scrollTop = 0;
      }
    });
    onMounted(() => {
      getAndUpdateData();
      cartAreaRef.value.addEventListener('scroll', () => {
        startTopDistance.value = cartAreaRef.value.scrollTop;
      });
      Bridge.registerFn('pageDidAppear', async () => {
        returnToCartPageHandler();
      });
    });
    onActivated(() => {
      store.commit(`navigator/setDistanceFromTop`, 0);
    });
    __expose({
      startTopDistance,
      cartAreaRef,
      returnToCartPageHandler
    });
    return (_ctx, _cache) => {
      const _component_amz_pull_refresh = _resolveComponent("amz-pull-refresh");
      const _component_BackTop = _resolveComponent("BackTop");
      const _directive_loading = _resolveDirective("loading");
      return _withDirectives((_openBlock(), _createElementBlock("div", {
        ref_key: "cartAreaRef",
        ref: cartAreaRef,
        class: "cart-area",
        style: _normalizeStyle({
          paddingBottom: cptTabbarHeight.value + 'px'
        })
      }, [_createVNode(CartTopNav, {
        cptPopupHeight: cptPopupHeight.value,
        isLoadingSelectAddressPopup: isLoadingSelectAddressPopup.value
      }, null, 8, ["cptPopupHeight", "isLoadingSelectAddressPopup"]), _createVNode(_component_amz_pull_refresh, {
        isLoad: _unref(isRefreshing),
        onOnRefresh: onPullRefresh
      }, {
        content: _withCtx(() => [_createVNode(CartTabs, {
          cptPopupHeight: cptPopupHeight.value
        }, null, 8, ["cptPopupHeight"]), _createVNode(RecommendedAsins, {
          ref_key: "recommendedAsinsRef",
          ref: recommendedAsinsRef,
          title: _ctx.$t('common.BEST_SELLING_ITEM_RECOMMENDATION'),
          onOnAddCartConfirm: onReloadData
        }, null, 8, ["title"])]),
        _: 1
      }, 8, ["isLoad"]), _unref(allProductData).length && !_unref(isHiddenBottomArea) ? (_openBlock(), _createElementBlock("div", _hoisted_1, [Number(_unref(freeShipping).amountFromFSThreshold) >= _unref(FREE_SHIPPING).SHOW_FREE_SHIPPING_PROMPT_THRESHOLD && !_unref(isManagement) ? (_openBlock(), _createBlock(FreeShippingTips, {
        key: 0,
        class: "collect-order",
        ref_key: "freeShippingTipsRef",
        ref: freeShippingTipsRef,
        style: _normalizeStyle({
          marginBottom: secondaryCartPageSafetyZoneHeight.value + 'px'
        })
      }, null, 8, ["style"])) : _createCommentVNode("", true), _createVNode(CheckoutNavigation, {
        ref_key: "totalPricesRef",
        ref: totalPricesRef
      }, null, 512)])) : _createCommentVNode("", true), _createVNode(DiscountDetails, {
        marginBottom: secondaryCartPageSafetyZoneHeight.value
      }, null, 8, ["marginBottom"]), _createVNode(_component_BackTop, {
        offsetTop: 200,
        style: _normalizeStyle({
          marginBottom: secondaryCartPageSafetyZoneHeight.value + 'px'
        })
      }, null, 8, ["style"])], 4)), [[_directive_loading, _unref(isLoading)]]);
    };
  }
});